import { useMemo, useCallback } from "react";

import { filterSupportedEntities } from "../entity-labels";
import { RegistryItem, SortState } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";

import {
  Entity,
  EntityType,
  useGetEmbeddingCount,
  useListFolderEntities,
} from "@/queries/folder-entities";
import { useChooseEntityConfig } from "@/components/ChooseEntityDialog/choose-entity-config-context";

const MIN_ENTITIES_FOR_SEMANTIC_SEARCH = 25;

interface UseRegistryDataProps {
  currentFolderId: number | null;
  workspaceId?: number | null;
}

const mapEntitiesToRegistryItems = (entities: Entity[] | undefined): RegistryItem[] =>
  (entities ?? []).map(e => e as RegistryItem);

const filterRegistryItemsForView = (
  items: RegistryItem[],
  currentFolderId: number | null,
): RegistryItem[] => (currentFolderId === null ? filterSupportedEntities(items) : items);

export const useRegistryData = ({ currentFolderId }: UseRegistryDataProps) => {
  const registry = useUnifiedRegistry();

  const hasSearchQuery = !!registry.debouncedSearchQuery;

  const { data: embeddingCountData } = useGetEmbeddingCount();
  const hasEnoughEmbeddings = (embeddingCountData?.count ?? 0) >= MIN_ENTITIES_FOR_SEMANTIC_SEARCH;

  const {
    data: textResponse,
    isLoading: isTextLoading,
    error: textError,
  } = useListFolderEntities({
    folder_id: currentFolderId,
    search_query: registry.debouncedSearchQuery,
    semantic_search: false,
    flatten: registry.hasActiveFilters,
    filter_types: registry.entityTypes,
    user_id: registry.selectedUserId,
    tags: registry.tags.length > 0 ? registry.tags : undefined,
    include_metadata: true,
    keepPreviousData: false,
  });

  const {
    data: semanticResponse,
    isLoading: isSemanticLoading,
    error: semanticError,
  } = useListFolderEntities({
    folder_id: currentFolderId,
    search_query: registry.debouncedSearchQuery,
    semantic_search: true,
    semantic_search_top_k: 10,
    flatten: registry.hasActiveFilters,
    filter_types: registry.entityTypes,
    user_id: registry.selectedUserId,
    tags: registry.tags.length > 0 ? registry.tags : undefined,
    include_metadata: true,
    keepPreviousData: false,
    enabled: hasSearchQuery && hasEnoughEmbeddings,
  });

  const isLoading = isTextLoading || (hasSearchQuery && hasEnoughEmbeddings && isSemanticLoading);
  const error = textError || semanticError;

  const registryItems: RegistryItem[] = useMemo(() => {
    const fromText = mapEntitiesToRegistryItems(textResponse?.entities);
    const mergedItems = (() => {
      if (!hasSearchQuery) {
        return fromText;
      }

      const semanticEntities = semanticResponse?.entities || [];
      const textEntityKeys = new Set(fromText.map(ent => `${ent.type}-${ent.id}`));
      const uniqueSemanticEntities = semanticEntities.filter(
        ent => !textEntityKeys.has(`${ent.type}-${ent.id}`),
      );

      return [...fromText, ...mapEntitiesToRegistryItems(uniqueSemanticEntities)];
    })();

    return filterRegistryItemsForView(mergedItems, currentFolderId);
  }, [textResponse?.entities, semanticResponse?.entities, hasSearchQuery, currentFolderId]);

  const chooseEntityConfig = useChooseEntityConfig();

  const sortByTypes = useMemo(
    () => (chooseEntityConfig && chooseEntityConfig.types) || [],
    [chooseEntityConfig],
  );

  const sortItems = useCallback((items: RegistryItem[], sortState?: SortState): RegistryItem[] => {
    if (!sortState) {
      return items;
    }

    const { field, direction } = sortState;

    return [...items].sort((a, b) => {
      let compareValue = 0;

      // eslint-disable-next-line default-case
      switch (field) {
        case "name":
          compareValue = (a.name || "").localeCompare(b.name || "");
          break;
        case "type":
          compareValue = a.type.localeCompare(b.type);
          break;
        case "modified": {
          const dateA = a.updated_at || a.created_at;
          const dateB = b.updated_at || b.created_at;
          if (!dateA && !dateB) {
            compareValue = 0;
          } else if (!dateA) {
            compareValue = -1;
          } else if (!dateB) {
            compareValue = 1;
          } else {
            compareValue = new Date(dateA).getTime() - new Date(dateB).getTime();
          }
          break;
        }
        case "created": {
          const createdA = a.created_at;
          const createdB = b.created_at;
          if (!createdA && !createdB) {
            compareValue = 0;
          } else if (!createdA) {
            compareValue = -1;
          } else if (!createdB) {
            compareValue = 1;
          } else {
            compareValue = new Date(createdA).getTime() - new Date(createdB).getTime();
          }
          break;
        }
      }

      return direction === "asc" ? compareValue : -compareValue;
    });
  }, []);

  const rawTextResultCount = useMemo(() => {
    if (!hasSearchQuery) {
      return 0;
    }
    const textEntities = textResponse?.entities || [];
    if (!registry.dateRange?.from && !registry.dateRange?.to) {
      return textEntities.length;
    }
    return textEntities.filter(item => {
      const dateToCheck = item.created_at;
      if (!dateToCheck) {
        return false;
      }
      const itemDate = new Date(dateToCheck);
      if (registry.dateRange?.from && itemDate < registry.dateRange.from) {
        return false;
      }
      if (registry.dateRange?.to && itemDate > registry.dateRange.to) {
        return false;
      }
      return true;
    }).length;
  }, [textResponse?.entities, hasSearchQuery, registry.dateRange]);

  const sortedAndFilteredItems = useMemo(() => {
    let filtered = [...registryItems];

    if (registry.dateRange?.from || registry.dateRange?.to) {
      filtered = filtered.filter(item => {
        const dateToCheck = item.created_at;
        if (!dateToCheck) {
          return false;
        }
        const itemDate = new Date(dateToCheck);
        const fromDate = registry.dateRange?.from;
        const toDate = registry.dateRange?.to;

        if (fromDate && itemDate < fromDate) {
          return false;
        }
        if (toDate && itemDate > toDate) {
          return false;
        }
        return true;
      });
    }

    if (hasSearchQuery) {
      const textResults = filtered.slice(0, rawTextResultCount);
      const semanticResults = filtered.slice(rawTextResultCount);

      const sortByPriority = (a: RegistryItem, b: RegistryItem) => {
        if (sortByTypes.length > 0) {
          const aInPriority = sortByTypes.includes(a.type as EntityType);
          const bInPriority = sortByTypes.includes(b.type as EntityType);

          if (aInPriority && !bInPriority) {
            return -1;
          }
          if (!aInPriority && bInPriority) {
            return 1;
          }

          if (aInPriority && bInPriority) {
            const aIndex = sortByTypes.indexOf(a.type as EntityType);
            const bIndex = sortByTypes.indexOf(b.type as EntityType);
            if (aIndex !== bIndex) {
              return aIndex - bIndex;
            }
          }
        }
        return 0;
      };

      return [...textResults.sort(sortByPriority), ...semanticResults.sort(sortByPriority)];
    }

    if (registry.sortState) {
      return sortItems(filtered, registry.sortState);
    }

    return filtered.sort((a, b) => {
      // First, prioritize by type if sortByTypes is configured
      if (sortByTypes.length > 0) {
        const aInPriority = sortByTypes.includes(a.type as EntityType);
        const bInPriority = sortByTypes.includes(b.type as EntityType);

        if (aInPriority && !bInPriority) {
          return -1;
        }
        if (!aInPriority && bInPriority) {
          return 1;
        }

        // If both are in priority types, sort by their order in sortByTypes
        if (aInPriority && bInPriority) {
          const aIndex = sortByTypes.indexOf(a.type as EntityType);
          const bIndex = sortByTypes.indexOf(b.type as EntityType);
          if (aIndex !== bIndex) {
            return aIndex - bIndex;
          }
        }
      }

      // Then sort by date (default sort)
      const dateA =
        a.updated_at || a.created_at ? new Date(a.updated_at || a.created_at!).getTime() : 0;
      const dateB =
        b.updated_at || b.created_at ? new Date(b.updated_at || b.created_at!).getTime() : 0;
      return dateB - dateA;
    });
  }, [
    registryItems,
    registry.dateRange,
    registry.sortState,
    sortItems,
    sortByTypes,
    hasSearchQuery,
    rawTextResultCount,
  ]);

  const textResultCount = useMemo(() => {
    if (!hasSearchQuery) {
      return 0;
    }
    const textResults = sortedAndFilteredItems.slice(0, rawTextResultCount);
    return textResults.filter(item => item.type !== EntityType.FOLDER).length;
  }, [sortedAndFilteredItems, rawTextResultCount, hasSearchQuery]);

  const folders = useMemo(
    () => sortedAndFilteredItems.filter(item => item.type === EntityType.FOLDER),
    [sortedAndFilteredItems],
  );

  const items = useMemo(
    () => sortedAndFilteredItems.filter(item => item.type !== EntityType.FOLDER),
    [sortedAndFilteredItems],
  );

  const allItems = useMemo(() => sortedAndFilteredItems, [sortedAndFilteredItems]);

  return {
    folders,
    items,
    totalCount: registryItems.length,
    isLoading: isLoading || registry.isPendingDebounce,
    error,
    allItems,
    sortState: registry.sortState,
    textResultCount,
  };
};
